// SUB-OFFICE 7 — ADJUDICATION. The latency ceremony: the model's thinking
// time, staged as the building leaning closer. The plunger slam has already
// fired the POST; this module owns everything between the slam and the slip.
//
// FULL (first 5 lifetime, any DENIED, clemency, grade changes, formality):
//   t=0.0  hold line prints; the player's canister launches (Vault at t=4.0)
//   t=0.2  hall dims to 50% over 400ms; post vignette/grain ramp; every iris
//          dilates; the hum deepens a fifth
//   t=4.0  HARD FLOOR — no verdict may exist before the building has finished
//          leaning in, however fast the Machine was
//   t=4.5+ hold lines at a dignified cadence
//   t=10   'THE MACHINE IS CONFERRING WITH ITSELF.' (doubles as retry mask)
//   t=15   QUEUED conversion — the offering becomes a chit; the verdict will
//          find the drawer (M5-tuned constant, default 15s)
// EXPEDITED: 2s floor, no dim, no iris storm, single nearest lamp turn. The
// building has stopped performing for you, which is worse.
//
// Movement during the wait is measured in meters and returned to the caller —
// it becomes the slip's observation line and the /api/ack payload.

import { nextHoldLine, CONFER_LINES } from '../../shared/copy';
import type { CeremonyTier } from '../../shared/types';
import { MovementMeter } from '../telemetry';
import { Timeline, CeremonyDirector, delay } from './director';
import type { ApiResult } from '../net';
import type { EventBus } from '../entities/entities';
import type { SceneManager } from '../render/scene';
import type { PostPipeline } from '../render/post';
import type { EyeLamps } from '../entities/lamps';
import type { Canisters } from '../entities/canisters';
import type { Player } from '../entities/player';

export const QUEUED_WINDOW_S = 15; // M5-tuned conversion constant
const FLOOR_FULL_S = 4;
const FLOOR_EXPEDITED_S = 2;

export interface AdjudicationOutcome<T> {
  kind: 'result' | 'queued-timeout' | 'failed';
  response: ApiResult<T> | null;
  movedM: number;
  stoodStill: boolean;
}

export class AdjudicationCeremony {
  private meter: MovementMeter;
  private director = new CeremonyDirector();
  private staged = false;

  constructor(
    private bus: EventBus,
    private sceneMgr: SceneManager,
    private post: PostPipeline,
    private lamps: EyeLamps,
    private canisters: Canisters,
    player: Player
  ) {
    this.meter = new MovementMeter(player);
  }

  /** Per-frame: staging cues + the movement meter. Called from the main loop. */
  update(dt: number): void {
    this.director.update(dt);
    this.meter.update();
  }

  /**
   * Stage the wait around an in-flight request. Resolves with the response
   * (never before the tier's hard floor) or converts to the chit path at the
   * QUEUED window. The caller has already slammed; the POST is already gone.
   */
  async run<T>(
    tier: CeremonyTier,
    request: Promise<ApiResult<T>>,
    opts: { windowS?: number } = {}
  ): Promise<AdjudicationOutcome<T>> {
    const windowS = opts.windowS ?? QUEUED_WINDOW_S;
    const floor = tier === 'FULL' ? FLOOR_FULL_S : FLOOR_EXPEDITED_S;
    this.meter.start();
    this.canisters.launchPlayerCanister(floor);
    this.bus.emit('audio:adjudication', { on: tier === 'FULL' });
    this.bus.emit('ceremony:adjudication', { tier }); // ghosts stop and turn

    if (tier === 'FULL') this.stageFull();
    // EXPEDITED restraint: no dim, no iris storm; the nearest lamp is already
    // tracking (it never stopped — familiarity is the Bureau's intimacy).

    let settled: ApiResult<T> | null = null;
    let failedHard = false;
    const tracked = request.then(
      (r) => {
        settled = r;
        return r;
      },
      () => {
        failedHard = true;
        return null;
      }
    );

    const t0 = performance.now();
    const elapsed = (): number => (performance.now() - t0) / 1000;

    // the hard floor: the ceremony refuses to be shorter than itself
    while (elapsed() < floor) await delay(50);
    // then wait for the Machine, converting to a chit at the tuned window
    while (settled === null && !failedHard && elapsed() < windowS) {
      await delay(80);
    }

    const moved = this.meter.stop();
    await this.release(tier);

    if (settled !== null) {
      return { kind: 'result', response: settled, ...moved };
    }
    if (failedHard) {
      return { kind: 'failed', response: null, ...moved };
    }
    // past the window: the request continues server-side; the verdict is
    // written delivered=0 and claimed by the next /api/ack or docket read.
    void tracked;
    return { kind: 'queued-timeout', response: null, ...moved };
  }

  /** The full staging: dim, convergence, hold lines, the confer beat. */
  private stageFull(): void {
    this.staged = true;
    const tl = new Timeline();
    tl.at(0, () => this.bus.emit('chyron', nextHoldLine()));
    tl.at(0.2, () => {
      // 50% dim over 400ms: 0.5 delta / 0.4s = rate 1.25
      this.sceneMgr.setDim(0.5, 1.25);
      this.lamps.setIris(0); // every iris dilates
      this.lamps.setConverge(true); // heads tilt down at you (the FP view sees it)
    });
    tl.lerp(0.2, 0.8, (k) => this.post.setAdjudication(k));
    tl.at(4.6, () => this.bus.emit('chyron', nextHoldLine()));
    tl.at(8, () => this.bus.emit('chyron', nextHoldLine()));
    tl.at(10, () => this.bus.emit('chyron', CONFER_LINES[Math.floor(Math.random() * CONFER_LINES.length)]));
    tl.at(13, () => this.bus.emit('chyron', nextHoldLine()));
    void this.director.play(tl);
  }

  /** Verdict release: light snaps back in 200ms — the Bureau's applause. */
  private async release(tier: CeremonyTier): Promise<void> {
    this.director.stop();
    this.bus.emit('audio:adjudication', { on: false });
    this.bus.emit('ceremony:verdict'); // the murmuration disperses
    if (tier === 'FULL' || this.staged) {
      this.staged = false;
      this.sceneMgr.snapDim();
      this.lamps.setIris(1);
      this.lamps.setConverge(false);
      const tl = new Timeline();
      tl.lerp(0, 0.25, (k) => this.post.setAdjudication(1 - k));
      await this.director.play(tl);
    }
  }

  /**
   * Escalation: an expedited wait that turned out to deserve reverence
   * (first DENIED, grade change, clemency — the server said FULL). The hall
   * performs a compressed convergence before the slip is permitted to print.
   */
  async escalateToFull(): Promise<void> {
    this.bus.emit('audio:adjudication', { on: true });
    const tl = new Timeline();
    tl.at(0, () => {
      this.sceneMgr.setDim(0.5, 1.25);
      this.lamps.setIris(0);
      this.lamps.setConverge(true);
      this.bus.emit('chyron', nextHoldLine());
    });
    tl.lerp(0, 0.5, (k) => this.post.setAdjudication(k));
    tl.at(2.6, () => {
      /* the pause is the point */
    });
    await this.director.play(tl);
    this.bus.emit('audio:adjudication', { on: false });
    this.sceneMgr.snapDim();
    this.lamps.setIris(1);
    this.lamps.setConverge(false);
    const out = new Timeline();
    out.lerp(0, 0.25, (k) => this.post.setAdjudication(1 - k));
    await this.director.play(out);
  }

  /**
   * The verdict dolly: fov 22 -> 17 over 1.8s (full ceremony only). In the
   * FP vantage the same lean-in plays proportionally (70 -> ~48): the slip
   * prints close-up without the long lens's extremity. Returns a cleanup that
   * restores the lens when the slip is acknowledged.
   */
  dollyIn(): () => void {
    const mgr = this.sceneMgr;
    let t = 0;
    let closing = false;
    let closeT = 0;
    let baseFov = 22;
    let fovTarget = 17;
    let captured = false;
    mgr.pushCue((dt, camera) => {
      if (!captured) {
        captured = true;
        baseFov = camera.fov;
        fovTarget = baseFov > 40 ? Math.max(40, baseFov - 22) : 17;
      }
      if (!closing) {
        t = Math.min(1.8, t + dt);
        camera.fov = baseFov + (fovTarget - baseFov) * (t / 1.8);
        camera.updateProjectionMatrix();
        return true;
      }
      closeT = Math.min(0.4, closeT + dt);
      camera.fov = fovTarget + (baseFov - fovTarget) * (closeT / 0.4);
      camera.updateProjectionMatrix();
      return closeT < 0.4;
    });
    return () => {
      closing = true;
    };
  }
}
